import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { SfAccountSnapshot, SyncStatus, RateLimit, ProvisioningRequestSummary } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AccountStatusBadge } from '@/components/AccountStatusBadge';
import { StatusBadge } from '@/components/StatusBadge';
import { SyncStatusBanner } from '@/components/SyncStatusBanner';
import { RefreshButton } from '@/components/RefreshButton';
import { formatDate } from '@/lib/utils';
import { Plus, Building2, Loader2 } from 'lucide-react';

export function ResellerDashboard() {
  const [accounts, setAccounts] = useState<SfAccountSnapshot[]>([]);
  const [provisioningRequests, setProvisioningRequests] = useState<ProvisioningRequestSummary[]>([]);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [rateLimit, setRateLimit] = useState<RateLimit | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    setIsLoading(true);
    try {
      const response = await api.getAccounts();
      setAccounts(response.accounts);
      setProvisioningRequests(response.provisioning_requests || []);
      setSyncStatus(response.sync_status);
      setRateLimit(response.rate_limit);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const totalCount = accounts.length + provisioningRequests.length;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await api.refreshAccounts();
      if (response.success) {
        await loadAccounts();
      } else {
        console.error('Refresh failed:', response.error);
      }
      setRateLimit({
        remaining_attempts: response.remaining_attempts,
        reset_at: response.reset_at,
      });
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Accounts</h1>
            <p className="text-gray-600 mt-1">Manage your accounts</p>
          </div>
          <Link to="/requests/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Account
            </Button>
          </Link>
        </div>

        {syncStatus && <SyncStatusBanner syncStatus={syncStatus} />}

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>Accounts</CardTitle>
                <CardDescription>
                  {totalCount} account{totalCount !== 1 ? 's' : ''}
                  {provisioningRequests.length > 0 && ` (${provisioningRequests.length} pending)`}
                </CardDescription>
              </div>
              {rateLimit && (
                <RefreshButton
                  onClick={handleRefresh}
                  loading={isRefreshing}
                  rateLimit={rateLimit}
                />
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
              </div>
            ) : totalCount === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Building2 className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">No accounts found</p>
                <Link to="/requests/new" className="mt-4">
                  <Button variant="outline">Provision your first account</Button>
                </Link>
              </div>
            ) : (
              <>
                {/* Mobile/tablet card view */}
                <div className="lg:hidden space-y-4">
                  {/* Provisioning requests first (pending accounts) */}
                  {provisioningRequests.map((request) => (
                    <div key={request.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-medium text-gray-900">
                            {request.payload.Account_Name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {request.payload.Industry}
                          </p>
                        </div>
                        <StatusBadge status={request.status} />
                      </div>
                      {request.last_error && (
                        <p className="text-xs text-red-600 mb-3 break-words">
                          {request.last_error}
                        </p>
                      )}
                      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                        <div>
                          <p className="text-gray-500">Contact</p>
                          <p className="text-gray-900">{request.payload.First_Name} {request.payload.Last_Name}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Email</p>
                          <p className="text-gray-900 break-all">{request.payload.Email}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500">
                          Created {formatDate(request.created_at)}
                        </p>
                        <Link to={`/requests/${request.id}`}>
                          <Button variant="ghost" size="sm">
                            View
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                  {/* Synced accounts */}
                  {accounts.map((account) => (
                    <div key={account.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-medium text-gray-900">
                            {account.account_name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {account.sf_account_id}
                          </p>
                        </div>
                        <AccountStatusBadge status={account.account_status_ai} />
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                        <div>
                          <p className="text-gray-500">Abby Assigned Phone Number</p>
                          <p className="text-gray-900">{account.assigned_phone_number || '-'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">AI Minutes Used</p>
                          <p className="text-gray-900">
                            {account.ai_minutes_used_current_cycle !== null
                              ? account.ai_minutes_used_current_cycle.toLocaleString()
                              : '-'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500">
                          Updated {formatDate(account.updated_at)}
                        </p>
                        <Link to={`/accounts/${account.sf_account_id}`}>
                          <Button variant="ghost" size="sm">
                            View
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop table view */}
                <div className="hidden lg:block">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm text-gray-500">
                        <th className="pb-3 font-medium">Account Name</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Abby Assigned Phone Number</th>
                        <th className="pb-3 font-medium">AI Minutes Used</th>
                        <th className="pb-3 font-medium">Last Updated</th>
                        <th className="pb-3 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {/* Provisioning requests first (pending accounts) */}
                      {provisioningRequests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="py-4">
                            <div>
                              <p className="font-medium text-gray-900">
                                {request.payload.Account_Name}
                              </p>
                              <p className="text-sm text-gray-500">
                                {request.payload.Industry}
                              </p>
                            </div>
                          </td>
                          <td className="py-4">
                            <StatusBadge status={request.status} />
                            {request.last_error && (
                              <p className="text-xs text-red-600 mt-1 max-w-[200px] truncate" title={request.last_error}>
                                {request.last_error}
                              </p>
                            )}
                          </td>
                          <td className="py-4 text-sm text-gray-600">
                            -
                          </td>
                          <td className="py-4 text-sm text-gray-600">
                            -
                          </td>
                          <td className="py-4 text-sm text-gray-500">
                            {formatDate(request.created_at)}
                          </td>
                          <td className="py-4">
                            <Link to={`/requests/${request.id}`}>
                              <Button variant="ghost" size="sm">
                                View
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                      {/* Synced accounts */}
                      {accounts.map((account) => (
                        <tr key={account.id} className="hover:bg-gray-50">
                          <td className="py-4">
                            <div>
                              <p className="font-medium text-gray-900">
                                {account.account_name}
                              </p>
                              <p className="text-sm text-gray-500">
                                {account.sf_account_id}
                              </p>
                            </div>
                          </td>
                          <td className="py-4">
                            <AccountStatusBadge status={account.account_status_ai} />
                          </td>
                          <td className="py-4 text-sm text-gray-600">
                            {account.assigned_phone_number || '-'}
                          </td>
                          <td className="py-4 text-sm text-gray-600">
                            {account.ai_minutes_used_current_cycle !== null
                              ? account.ai_minutes_used_current_cycle.toLocaleString()
                              : '-'}
                          </td>
                          <td className="py-4 text-sm text-gray-500">
                            {formatDate(account.updated_at)}
                          </td>
                          <td className="py-4">
                            <Link to={`/accounts/${account.sf_account_id}`}>
                              <Button variant="ghost" size="sm">
                                View
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
